// src/components/cookie-consent/deduplication.ts
'use client'

// Event deduplication system to prevent duplicate tracking
interface TrackedEvent {
    eventType: string;
    eventData: string; // JSON stringified event data
    timestamp: number;
    sentVia: 'gtag' | 'dataLayer' | 'both';
}

class EventDeduplicator {
    private trackedEvents: Map<string, TrackedEvent> = new Map();
    private readonly DEDUP_WINDOW_MS = 1000; // 1 second deduplication window
    private readonly MAX_TRACKED_EVENTS = 100; // Prevent memory leaks

    // Check if event was recently tracked
    public isDuplicate(eventType: string, eventData: any, method: 'gtag' | 'dataLayer'): boolean {
        const eventKey = this.generateEventKey(eventType, eventData);
        const now = Date.now();
        const existing = this.trackedEvents.get(eventKey);

        if (!existing) {
            return false; // Not a duplicate - first time seeing this event
        }

        // Check if within deduplication window
        if (now - existing.timestamp > this.DEDUP_WINDOW_MS) {
            return false; // Outside deduplication window, allow it
        }

        // It's within the window - check if it's the same method or different
        if (existing.sentVia === method) {
            console.warn(`⚠️ [Dedup] Blocked duplicate ${eventType} via ${method}:`, {
                eventKey,
                timeSinceFirst: now - existing.timestamp,
                originalMethod: existing.sentVia
            });
            return true; // Exact duplicate
        }

        // Different method - update the record to show both methods used
        existing.sentVia = 'both';
        existing.timestamp = now;

        console.log(`📊 [Dedup] Allowing ${eventType} via ${method} (complement to ${existing.sentVia === 'both' ? 'other method' : existing.sentVia})`);
        return false; // Allow complementary method
    }

    // Record that an event was tracked
    public recordEvent(eventType: string, eventData: any, method: 'gtag' | 'dataLayer'): void {
        const eventKey = this.generateEventKey(eventType, eventData);
        const now = Date.now();

        this.trackedEvents.set(eventKey, {
            eventType,
            eventData: JSON.stringify(eventData),
            timestamp: now,
            sentVia: method
        });

        // Clean up old events to prevent memory leaks
        this.cleanupOldEvents(now);
    }

    // Get debug information
    public getDebugInfo(): any {
        return {
            trackedEventsCount: this.trackedEvents.size,
            dedupWindowMs: this.DEDUP_WINDOW_MS,
            recentEvents: Array.from(this.trackedEvents.entries()).slice(-5).map(([key, event]) => ({
                key,
                type: event.eventType,
                method: event.sentVia,
                age: Date.now() - event.timestamp
            }))
        };
    }

    // Reset for testing
    public reset(): void {
        this.trackedEvents.clear();
    }

    // Generate a unique key for an event
    private generateEventKey(eventType: string, eventData: any): string {
        // Create a normalized version of event data for comparison
        const normalizedData = {
            ...eventData,
            // Remove timestamp and tracking metadata that shouldn't affect uniqueness
            timestamp: undefined,
            tracking_version: undefined,
            server_hostname: undefined
        };

        return `${eventType}:${JSON.stringify(normalizedData)}`;
    }

    // Clean up events older than deduplication window
    private cleanupOldEvents(currentTime: number): void {
        if (this.trackedEvents.size <= this.MAX_TRACKED_EVENTS) return;

        const cutoffTime = currentTime - this.DEDUP_WINDOW_MS * 2;

        for (const [key, event] of this.trackedEvents.entries()) {
            if (event.timestamp < cutoffTime) {
                this.trackedEvents.delete(key);
            }
        }
    }
}

// Global deduplicator instance
export const eventDeduplicator = new EventDeduplicator();

// Helper functions for easy usage
export const shouldTrackEvent = (eventType: string, eventData: any, method: 'gtag' | 'dataLayer'): boolean => {
    return !eventDeduplicator.isDuplicate(eventType, eventData, method);
};

export const recordTrackedEvent = (eventType: string, eventData: any, method: 'gtag' | 'dataLayer'): void => {
    eventDeduplicator.recordEvent(eventType, eventData, method);
};

export const getDeduplicationDebugInfo = () => eventDeduplicator.getDebugInfo();
